/**
 * @file components/decisions/DossierCard.tsx
 * @description 稽核文件包卡片（驗證決定詳情頁）。
 *
 * 渲染報告核准移交時凍結的 dossier_snapshot：終稿報告指針、案件附件（可下載）、
 * 稽核任務/記錄摘要、不符合事項、最新風險評估。舊決定無快照時顯示提示。
 */
import { Alert, Card, Descriptions, Empty, List, Space, Tag, Typography } from 'antd';
import { Download, FolderOpen } from 'lucide-react';
import type { DossierSnapshot } from '@zhcb/shared';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';

interface Props {
  snapshot: DossierSnapshot | null;
}

/** 下載附件：取預簽名 URL 後新窗口打開 */
async function downloadFile(fileId: number) {
  try {
    const res = await apiClient.get<{ url: string }>(`/api/files/${fileId}/download`);
    window.open(res.url, '_blank', 'noopener');
  } catch (e) {
    void message.error((e as Error).message || '取得下載連結失敗');
  }
}

/** 稽核文件包卡片：分組展示移交快照 */
export function DossierCard({ snapshot }: Props) {
  return (
    <Card
      size="small"
      title={
        <Space size={6}>
          <FolderOpen size={14} />
          <span>稽核文件包</span>
          {snapshot && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              移交於 {new Date(snapshot.snapshotAt).toLocaleString()}
            </Typography.Text>
          )}
        </Space>
      }
    >
      {!snapshot ? (
        <Empty
          description="本決定建立於文件包功能上線前，無移交快照"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* 終稿報告指針 */}
          <Descriptions column={3} size="small" title="稽核報告（終稿）">
            <Descriptions.Item label="報告編號">
              <span style={{ fontFamily: 'monospace' }}>{snapshot.report.code}</span>
            </Descriptions.Item>
            <Descriptions.Item label="版本">v{snapshot.report.version}</Descriptions.Item>
            <Descriptions.Item label="終稿時間">
              {new Date(snapshot.report.finalizedAt).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>

          {/* 案件附件（可下載） */}
          <List
            size="small"
            header={<Typography.Text strong>案件文件（{snapshot.files.length}）</Typography.Text>}
            dataSource={snapshot.files}
            locale={{ emptyText: '無案件附件' }}
            renderItem={(f) => (
              <List.Item
                actions={[
                  <Typography.Link key="dl" onClick={() => void downloadFile(f.fileId)}>
                    <Download size={12} style={{ verticalAlign: -2 }} /> 下載
                  </Typography.Link>,
                ]}
              >
                <Space size={8}>
                  <Tag>{f.docType}</Tag>
                  <span>{f.filename}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {(f.sizeBytes / 1024).toFixed(0)} KB
                  </Typography.Text>
                </Space>
              </List.Item>
            )}
          />

          {/* 稽核任務 / 記錄摘要 */}
          <Descriptions column={2} size="small" title="稽核活動">
            <Descriptions.Item label="稽核任務">
              {snapshot.auditTasks.length === 0
                ? '—'
                : snapshot.auditTasks
                    .map(
                      (t) =>
                        `${t.assigneeName ?? `#${t.id}`}${t.isTeamLead ? '（組長）' : ''}·${t.status}`,
                    )
                    .join('、')}
            </Descriptions.Item>
            <Descriptions.Item label="稽核記錄">
              {snapshot.auditRecords.length} 筆
            </Descriptions.Item>
          </Descriptions>

          {/* NC 清單 */}
          <List
            size="small"
            header={
              <Typography.Text strong>不符合事項（{snapshot.ncFindings.length}）</Typography.Text>
            }
            dataSource={snapshot.ncFindings}
            locale={{ emptyText: '無不符合事項' }}
            renderItem={(n) => (
              <List.Item>
                <Space size={8}>
                  <Tag color={n.closed ? 'green' : 'red'}>{n.closed ? '已關閉' : '未關閉'}</Tag>
                  <Tag>{n.severity}</Tag>
                  <Typography.Text>{n.description}</Typography.Text>
                </Space>
              </List.Item>
            )}
          />

          {/* 風險評估 */}
          {snapshot.riskAssessment ? (
            <Descriptions column={3} size="small" title="風險評估（最新）">
              <Descriptions.Item label="分數">
                {snapshot.riskAssessment.riskScore}
              </Descriptions.Item>
              <Descriptions.Item label="建議">
                {snapshot.riskAssessment.recommendation ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="評估時間">
                {new Date(snapshot.riskAssessment.assessedAt).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Alert type="info" showIcon message="移交時無風險評估記錄" />
          )}
        </Space>
      )}
    </Card>
  );
}
